// Money management helpers — pure, no Firebase / Chakra imports so they
// can be reused by the UI, the purchase wrapper, and (later) any
// server-side accounting jobs without dragging extra dependencies.
//
// Convention: every amount in this file is in **cents** (integer).
// Callers convert from `event.cost` / `event.venueMinimumRevenue`
// (stored in dollars) at the boundary with `Math.round(value * 100)`.
//
// Rounding always falls into the platform bucket so
//   `venueShare + producerShare + platformShare === paidAmount`
// holds exactly. That means the venue and producer never get short-
// changed by half a cent, and the platform's earnings absorb the
// tiny rounding noise.

// Splits used when the event is tied to a partner venue. Past the venue
// minimum, Proximy keeps the larger share to cover platform/venue risk.
export const PRODUCER_SHARE_AFTER_VENUE = 0.3;
export const PLATFORM_SHARE_AFTER_VENUE = 0.7;

// Splits used when the event has no partner venue (the host either owns
// the space or it's a no-venue event). The host carries the planning so
// they keep the larger share — Proximy only takes a platform/app fee.
export const PRODUCER_SHARE_NO_VENUE = 0.7;
export const PLATFORM_SHARE_NO_VENUE = 0.3;

const toCents = (dollars) => Math.round((Number(dollars) || 0) * 100);

// Walks a single ticket purchase through the waterfall.
//
//   1. Anything that fits below the remaining venue minimum goes 100%
//      to the venue.
//   2. Everything past the venue minimum gets split between the producer
//      (host/event poster) and the platform:
//      - With a partner venue → 30% producer / 70% platform.
//      - No partner venue     → 70% producer / 30% platform (app fee).
//
// One ticket can straddle the boundary — e.g. venue is $9 short of its
// minimum and the ticket is $20: the first $9 covers the venue, the
// remaining $11 splits per the rule above.
export const computeTicketSplit = ({
  ticketPrice,
  runningRevenue,
  venueMinimum,
}) => {
  const price = Math.max(0, Math.round(Number(ticketPrice) || 0));
  if (price === 0) {
    return { venueShare: 0, producerShare: 0, platformShare: 0 };
  }

  const minimum = Math.max(0, Math.round(Number(venueMinimum) || 0));
  const collected = Math.max(0, Math.round(Number(runningRevenue) || 0));
  const remainingVenue = Math.max(0, minimum - collected);
  const venueShare = Math.min(price, remainingVenue);
  const remainder = price - venueShare;

  const producerShareRate =
    minimum > 0 ? PRODUCER_SHARE_AFTER_VENUE : PRODUCER_SHARE_NO_VENUE;
  const producerShare = Math.round(remainder * producerShareRate);
  const platformShare = remainder - producerShare;

  return { venueShare, producerShare, platformShare };
};

// Snapshot of where the event's revenue is sitting right now. Used by
// the UI panels — the producer wants to see how much they've earned,
// the venue owner wants to see how much of their fee is covered.
//
// Falls back to a synthetic running total when the per-event counters
// haven't been written yet (legacy events created before Phase 1) so
// the numbers don't suddenly read as $0 for events that already have
// attendees on them.
export const summarizeEventMoney = (event) => {
  if (!event) {
    return {
      collected: 0,
      venueMinimum: 0,
      venueCovered: 0,
      venueRemaining: 0,
      producerEarned: 0,
      platformEarned: 0,
      venueProgress: 0,
      hasCounters: false,
    };
  }

  const venueMinimum = toCents(event.venueMinimumRevenue);
  const ticketPrice = toCents(event.cost);

  // If the event doc has been touched by the new purchase pipeline,
  // trust those counters. Otherwise estimate from non-host attendees
  // so legacy data still renders something useful.
  const hasCounters =
    Number.isFinite(event.revenueCollected) &&
    event.revenueCollected !== null;

  let collected;
  let venueCovered;
  let producerEarned;
  let platformEarned;

  if (hasCounters) {
    collected = Math.max(0, Math.round(event.revenueCollected || 0));
    venueCovered = Math.max(0, Math.round(event.venueRevenueCollected || 0));
    producerEarned = Math.max(
      0,
      Math.round(event.producerRevenueCollected || 0)
    );
    platformEarned = Math.max(
      0,
      Math.round(event.platformRevenueCollected || 0)
    );
  } else {
    const paidTickets = nonHostAttendeeCount(event);
    collected = paidTickets * ticketPrice;
    venueCovered = Math.min(collected, venueMinimum);
    const remainder = collected - venueCovered;
    const producerShareRate =
      venueMinimum > 0 ? PRODUCER_SHARE_AFTER_VENUE : PRODUCER_SHARE_NO_VENUE;
    producerEarned = Math.round(remainder * producerShareRate);
    platformEarned = remainder - producerEarned;
  }

  const venueRemaining = Math.max(0, venueMinimum - venueCovered);
  const venueProgress =
    venueMinimum > 0 ? Math.min(1, venueCovered / venueMinimum) : 1;

  return {
    collected,
    venueMinimum,
    venueCovered,
    venueRemaining,
    producerEarned,
    platformEarned,
    venueProgress,
    hasCounters,
  };
};

// `event.members` is seeded with the host's userRef in
// `buildCreateEventPayload`, so `members.length` always overcounts the
// real number of paying tickets by exactly one (assuming the host is
// still in the array). Use this anywhere we compare attendees against
// a partner-venue minimum — the host doesn't fund their own venue fee.
export const nonHostAttendeeCount = (event) => {
  if (!event) return 0;
  const members = event.members || [];
  if (members.length === 0) return 0;
  if (!event.host) return members.length;
  const hostInMembers = members.some((m) => {
    if (!m) return false;
    if (typeof m === "string") return m === event.host;
    return m.id === event.host;
  });
  return hostInMembers ? Math.max(0, members.length - 1) : members.length;
};
